import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Mail } from 'lucide-react'
import styles from './ConversationalMode.module.scss'

type ColorTheme = 'blue' | 'green' | 'orange'

interface DataResultsContainerProps {
  icon: React.ReactNode
  title: string
  subtitle?: string
  color: ColorTheme
  total: number
  maxDisplay?: number
  viewAllLink?: string
  viewAllText?: string
  emptyMessage?: string
  emptyContactEmail?: string
  note?: React.ReactNode
  children: React.ReactNode
  isEmpty?: boolean
  openInNewWindow?: boolean
}

const colorClasses: Record<ColorTheme, {
  container: string
  header: string
  list: string
  note: string
  viewAll: string
  empty: string
  contactLink: string
}> = {
  blue: {
    container: styles.resultsContainerBlue,
    header: styles.resultsHeaderBlue,
    list: styles.resultsListBlue,
    note: styles.resultsNoteBlue,
    viewAll: styles.resultsViewAllBlue,
    empty: styles.resultsEmptyBlue,
    contactLink: styles.resultsContactLinkBlue
  },
  green: {
    container: styles.resultsContainerGreen,
    header: styles.resultsHeaderGreen,
    list: styles.resultsListGreen,
    note: styles.resultsNoteGreen,
    viewAll: styles.resultsViewAllGreen,
    empty: styles.resultsEmptyGreen,
    contactLink: styles.resultsContactLinkGreen
  },
  orange: {
    container: styles.resultsContainerOrange,
    header: styles.resultsHeaderOrange,
    list: styles.resultsListOrange,
    note: styles.resultsNoteOrange,
    viewAll: styles.resultsViewAllOrange,
    empty: styles.resultsEmptyOrange,
    contactLink: styles.resultsContactLinkOrange
  }
}

const DataResultsContainer: React.FC<DataResultsContainerProps> = ({
  icon,
  title,
  subtitle,
  color,
  total,
  maxDisplay = 3,
  viewAllLink,
  viewAllText,
  emptyMessage,
  emptyContactEmail,
  note,
  children,
  isEmpty = false,
  openInNewWindow = false
}) => {
  const navigate = useNavigate()
  const classes = colorClasses[color]

  if (isEmpty && emptyMessage) {
    return (
      <div className={`${styles.resultsContainer} ${classes.container}`}>
        <div className={`${styles.resultsEmpty} ${classes.empty}`}>
          {icon}
          <p>{emptyMessage}</p>
          {emptyContactEmail && (
            <a href={`mailto:${emptyContactEmail}`} className={`${styles.resultsContactLink} ${classes.contactLink}`}>
              <Mail size={16} />
              Escribinos a {emptyContactEmail}
            </a>
          )}
        </div>
      </div>
    )
  }

  if (isEmpty) {
    return null
  }

  return (
    <div className={`${styles.resultsContainer} ${classes.container}`}>
      <div className={`${styles.resultsHeader} ${classes.header}`}>
        {icon}
        <span>
          {title}
          {subtitle && <span className={styles.resultsSubtitle}>{subtitle}</span>}
        </span>
      </div>
      
      <div className={`${styles.resultsList} ${classes.list}`}>
        {children}
      </div>

      {note && (
        <div className={`${styles.resultsNote} ${classes.note}`}>
          <span>{note}</span>
        </div>
      )}

      {total > maxDisplay && viewAllLink && viewAllText && (
        <button
          type="button"
          className={`${styles.resultsViewAll} ${classes.viewAll}`}
          onClick={() => {
            if (openInNewWindow) {
              window.open(`${window.location.origin}${viewAllLink}`, '_blank')
            } else {
              navigate(viewAllLink)
            }
          }}
        >
          {viewAllText} →
        </button>
      )}
    </div>
  )
}

export default DataResultsContainer

